/* eslint-disable import/order */
import './importsWorkaround'
import './styles.css'
import './testCrasher'
import './globals'
import './devtools'
import './entities'
import customChannels from './customChannels'
import './globalDomListeners'
import './mineflayer/maps'
import './mineflayer/cameraShake'
import './shims/patchShims'
import './mineflayer/java-tester/index'
import './external'
import './appConfig'
import './mineflayer/timers'
import './mineflayer/plugins'
import './app/progressControllers'
import './app/gamepadCursor'
import { onGameLoad } from './inventoryWindows'
import initCollisionShapes from './getCollisionInteractionShapes'
import { Vec3 } from 'vec3'

import './scaleInterface'

import { options } from './optionsStorage'
import './reactUi'
import { lockUrl, onBotCreate } from './controls'
import './dragndrop'
import downloadAndOpenFile, { isInterestedInDownload } from './downloadAndOpenFile'

import mineflayer from 'mineflayer'

import debug from 'debug'

import {
  activeModalStack,
  activeModalStacks,
  hideModal,
  insertActiveModalStack,
  isGameActive,
  miscUiState,
  maybeCleanupAfterDisconnect,
} from './globalState'

import { setLoadingScreenStatus, lastConnectOptions } from './appStatus'
import { isCypress } from './standaloneUtils'

import { unsupportedLocalServerFeatures } from './createLocalServer'

import { onAppLoad, resourcepackReload, resourcePackState } from './resourcePack'
import CustomChannelClient from './customClient'
import { registerServiceWorker } from './serviceWorker'
import { appStatusState, quickDevReconnect, resetAppStatusState } from './react/AppStatusProvider'

import { watchFov } from './rendererUtils'
import './devReload'
import './water'
import { ConnectOptions, downloadOtherGameData, downloadAllMinecraftData, loadMinecraftData } from './connect'
import { subscribe } from 'valtio'
import { mainMenuState } from './react/MainMenuRenderApp'
import './mobileShim'
import { parseFormattedMessagePacket } from './botUtils'
import { appStartup } from './clientMods'
import { appQueryParams, appQueryParamsArray } from './appParams'
import { playerState } from './mineflayer/playerState'
import { states } from 'minecraft-protocol'
import { initMotionTracking } from './react/uiMotion'
import { ReplayOpenError, ReplayVersionError } from './mineflayer/userError'
import { showErrorScreen } from './react/errors/errorScreenState'
import { startLocalReplayServer } from './packetsReplay/replayPackets'
import { getReplayOrbitCameraAngles } from './cameraRotationControls'
import { createFullScreenProgressReporter, createWrappedProgressReporter, ProgressReporter } from './core/progressReporter'
import { tryHandleBuiltinCommand } from './builtinCommands'
import { loadingTimerState } from './react/LoadingTimer'
import { versionToNumber } from 'mc-assets/dist/utils'
import { isPlayground } from './playgroundIntegration'
import { appLoadBackend } from './appViewerLoad'
import { FORBIDDEN_VERSION_THRESHOLD } from './supportedVersions.mjs'

window.debug = debug
window.beforeRenderFrame = []

// ACTUAL CODE

if (!isPlayground) {
  void appLoadBackend()
}
if (isPlayground) {
  void import('renderer/playground/playground')
}

void registerServiceWorker().then(() => {
  mainMenuState.serviceWorkerLoaded = true
})
watchFov()
initCollisionShapes()
onAppLoad()
customChannels()

if (appQueryParams.testCrashApp === '2') throw new Error('test')

function hideCurrentScreens () {
  activeModalStacks['main-menu'] = [...activeModalStack]
  insertActiveModalStack('', [])
}

function returnToMainMenuAfterDisconnect () {
  try {
    setLoadingScreenStatus(undefined)
  } catch {
  }
  resetAppStatusState()
  maybeCleanupAfterDisconnect()

  miscUiState.gameLoaded = false
  miscUiState.loadedDataVersion = null
  window.loadedData = undefined

  if (activeModalStacks['main-menu']) {
    insertActiveModalStack('main-menu')
  } else {
    insertActiveModalStack('', [])
  }
}

function listenGlobalEvents () {
  window.addEventListener('connect', e => {
    const options = (e as CustomEvent).detail
    void connect(options)
  })
}

export async function connect (connectOptions: ConnectOptions) {
  if (miscUiState.gameLoaded) return

  if (sessionStorage.delayLoadUntilFocus) {
    await new Promise(resolve => {
      if (document.hasFocus()) {
        resolve(undefined)
      } else {
        window.addEventListener('focus', resolve)
      }
    })
  }
  if (sessionStorage.delayLoadUntilClick) {
    await new Promise(resolve => {
      window.addEventListener('click', resolve)
    })
  }

  maybeCleanupAfterDisconnect()

  appStatusState.showReconnect = false
  loadingTimerState.loading = true
  loadingTimerState.start = Date.now()
  miscUiState.hasErrors = false
  miscUiState.hadConnected = false
  lastConnectOptions.value = connectOptions
  lastConnectOptions.hadWorldLoaded = false

  miscUiState.singleplayer = false
  miscUiState.flyingSquid = false

  if (connectOptions.peerId || connectOptions.viewerWsConnect) {
    setLoadingScreenStatus('P2P and viewer WebSocket modes are disabled in replay-focused mode', true)
    return
  }

  if (connectOptions.singleplayer) {
    setLoadingScreenStatus('Singleplayer mode is disabled in replay-focused mode', true)
    return
  }

  if (connectOptions.server) {
    setLoadingScreenStatus('Direct server mode is disabled in replay-focused mode', true)
    return
  }

  if (!connectOptions.worldStateFileContents) {
    setLoadingScreenStatus('Only replay file mode is supported in replay-focused mode', true)
    return
  }

  const { multiplayerRenderDistance } = options
  const { username } = connectOptions

  console.log('using player username', username)

  hideCurrentScreens()
  const progress = createFullScreenProgressReporter()
  // base stage: what the modal falls back to between the nested loading stages
  const loggingInMsg = 'Loading replay'
  progress.beginStage('connect', loggingInMsg)
  setLoadingScreenStatus(loggingInMsg, false, true)

  let ended = false
  let bot!: typeof __type_bot
  const handleSessionEnd = (wasKicked = false) => {
    if (ended) return
    loadingTimerState.loading = false
    const { alwaysReconnect } = appQueryParams
    if ((!wasKicked && miscUiState.appConfig?.allowAutoConnect && appQueryParams.autoConnect && lastConnectOptions.hadWorldLoaded) || (alwaysReconnect)) {
      if (alwaysReconnect === 'quick' || alwaysReconnect === 'fast') {
        quickDevReconnect()
      } else {
        location.reload()
      }
    }
    errorAbortController.abort()
    ended = true
    progress.end()
    if (bot) {
      bot.end()
      // ensure mineflayer plugins receive this event for cleanup
      bot.emit('end', '')
    }

    try {
      localReplaySession?.server?.close?.()
    } catch {
    }

    miscUiState.disconnectedCleanup = {
      callback () {
        appViewer.resetBackend(true)
        window.localServer = window.server = undefined

        if (bot) {
          bot.removeAllListeners()
          bot._client.removeAllListeners()
          bot._client = {
            //@ts-expect-error
            write (packetName) {
              console.warn('Tried to write packet', packetName, 'after bot was destroyed')
            }
          }
          //@ts-expect-error
          window.bot = bot = undefined
        }
      },
      date: Date.now(),
      wasConnected: miscUiState.hadConnected
    }
  }

  let lastPacket = undefined as string | undefined
  const onPossibleErrorDisconnect = () => {
    if (lastPacket && bot?._client && bot._client.state !== states.PLAY) {
      appStatusState.descriptionHint = `Last Server Packet: ${lastPacket}`
    }
  }
  const handleError = (err) => {
    if (ended) {
      console.warn('Ignoring error after session end:', err)
      return
    }
    console.error(err)
    if (err === 'ResizeObserver loop completed with undelivered notifications.') {
      return
    }
    const errText = typeof err === 'string' ? err : (err?.message ?? String(err))
    if (typeof errText === 'string' && errText.includes('Received update for')) {
      return
    }
    if (isCypress()) throw err
    miscUiState.hasErrors = true
    if (miscUiState.gameLoaded) return
    // close all modals
    for (const modal of activeModalStack) {
      hideModal(modal)
    }

    // In replay mode there is nothing to reconnect to, so every failure that
    // reaches here is terminal and gets a dedicated screen rather than the
    // status line plus a reconnect affordance.
    setLoadingScreenStatus(undefined)
    if (err instanceof ReplayVersionError) {
      showErrorScreen('version-unsupported', errText, { version: err.version, maxVersion: err.maxVersion })
    } else if (err instanceof ReplayOpenError) {
      showErrorScreen('replay-open', errText)
    } else {
      showErrorScreen('unknown', errText)
    }
    onPossibleErrorDisconnect()
    handleSessionEnd()
  }

  // todo(hard): remove it!
  const errorAbortController = new AbortController()
  window.addEventListener('unhandledrejection', (e) => {
    if (e.reason.name === 'ServerPluginLoadFailure') {
      if (confirm(`Failed to load server plugin ${e.reason.pluginName} (invoking ${e.reason.pluginMethod}). Continue?`)) {
        return
      }
    }
    if (e.reason?.stack?.includes('chrome-extension://')) {
      // ignore issues caused by chrome extension
      return
    }
    handleError(e.reason)
  }, {
    signal: errorAbortController.signal
  })
  window.addEventListener('error', (e) => {
    handleError(e.message)
  }, {
    signal: errorAbortController.signal
  })

  const renderDistance = multiplayerRenderDistance
  let localReplaySession: Awaited<ReturnType<typeof startLocalReplayServer>> | undefined
  try {
    await progress.executeWithMessage('Downloading Minecraft data', 'download-mcdata', async () => {
      loadingTimerState.networkOnlyStart = Date.now()

      let downloadingAssets = [] as string[]
      const reportAssetDownload = (asset: string, isDone: boolean) => {
        if (isDone) {
          downloadingAssets = downloadingAssets.filter(a => a !== asset)
        } else {
          downloadingAssets.push(asset)
        }
        progress.setSubStage('download-mcdata', `(${downloadingAssets.join(', ')})`)
      }

      await Promise.all([
        downloadAllMinecraftData(reportAssetDownload),
        downloadOtherGameData(reportAssetDownload)
      ])
      loadingTimerState.networkOnlyStart = 0
    })

    let dataDownloaded = false
    const downloadMcData = async (version: string) => {
      if (dataDownloaded) return
      dataDownloaded = true
      appViewer.resourcesManager.currentConfig = { version, texturesVersion: options.useVersionsTextures || undefined }

      await progress.executeWithMessage(
        'Processing downloaded Minecraft data',
        async () => {
          await loadMinecraftData(version)
          await appViewer.resourcesManager.loadSourceData(version)
        }
      )

      await progress.executeWithMessage(
        'Applying user-installed resource pack',
        async () => {
          try {
            await resourcepackReload(true)
          } catch (err) {
            console.error(err)
            const doContinue = confirm('Failed to apply texture pack. See errors in the console. Continue?')
            if (!doContinue) {
              throw err
            }
          }
        }
      )

      await progress.executeWithMessage(
        'Preparing textures',
        async () => {
          await appViewer.resourcesManager.updateAssetsData({})
        }
      )
    }

    let finalVersion = connectOptions.botVersion

    // Check for forbidden versions (>= 1.21.7) due to critical world display issues
    const checkForbiddenVersion = (version: string | undefined) => {
      if (!version) return
      const versionNum = versionToNumber(version)
      const thresholdNum = versionToNumber(FORBIDDEN_VERSION_THRESHOLD)
      if (versionNum >= thresholdNum) {
        throw new ReplayVersionError(
          `Version ${version} is not supported due to critical world display issues. Please use version ${FORBIDDEN_VERSION_THRESHOLD} or earlier.`,
          version,
          FORBIDDEN_VERSION_THRESHOLD
        )
      }
    }

    checkForbiddenVersion(finalVersion)

    appViewer.inWorldRenderingConfig.instantCameraUpdate = false
    if (connectOptions.worldStateFileContents) {
      try {
        localReplaySession = await startLocalReplayServer(connectOptions.worldStateFileContents)
      } catch (err) {
        console.error(err)
        throw new ReplayOpenError(`Failed to start local replay server: ${err}`)
      }
      appViewer.inWorldRenderingConfig.instantCameraUpdate = false
      finalVersion = localReplaySession.version
      checkForbiddenVersion(finalVersion)
    }

    const initialLoadingText = 'Loading local replay server'
    progress.setMessage(initialLoadingText)

    if (!localReplaySession) {
      throw new ReplayOpenError('Failed to initialize replay session')
    }

    if (finalVersion) {
      // ensure data is downloaded
      loadingTimerState.networkOnlyStart ??= Date.now()
      await downloadMcData(finalVersion)
    }

    bot = mineflayer.createBot({
      host: '127.0.0.1',
      version: finalVersion || undefined,
      ...localReplaySession ? {
        keepAlive: false,
      } : {},
      ...localReplaySession ? {
        connect () { },
        Client: CustomChannelClient as any,
      } : {},
      username,
      viewDistance: renderDistance,
      checkTimeoutInterval: 240 * 1000,
      // noPongTimeout: 240 * 1000,
      closeTimeout: 240 * 1000,
      respawn: options.autoRespawn,
      maxCatchupTicks: 0,
      'mapDownloader-saveToFile': false,
      // "mapDownloader-saveInternal": false, // do not save into memory, todo must be implemeneted as we do really care of ram
    }) as unknown as typeof __type_bot
    window.bot = bot

    customEvents.emit('mineflayerBotCreated')
    const _supportFeature = bot.supportFeature
    bot.supportFeature = ((feature) => {
      if (unsupportedLocalServerFeatures.includes(feature)) {
        return false
      }
      return _supportFeature(feature)
    }) as typeof bot.supportFeature

    bot.emit('inject_allowed')
    bot._client.emit('connect')
  } catch (err) {
    handleError(err)
  }
  if (!bot) return

  // everything the replay needs is loaded by now; the only thing left is the
  // user starting playback, which is what feeds the bot its first packets
  appStatusState.waitingForPlay = true

  // bot.on('inject_allowed', () => {
  //   loadingScreen.maybeRecoverable = false
  // })

  bot.on('error', handleError)

  bot.on('kicked', (kickReason) => {
    console.log('You were kicked!', kickReason)
    const { formatted: kickReasonFormatted, plain: kickReasonString } = parseFormattedMessagePacket(kickReason)
    // close all modals
    for (const modal of activeModalStack) {
      hideModal(modal)
    }
    const shouldHideDisconnectScreen = miscUiState.gameLoaded || lastConnectOptions.hadWorldLoaded
    if (shouldHideDisconnectScreen) {
      handleSessionEnd(true)
      returnToMainMenuAfterDisconnect()
      return
    }

    setLoadingScreenStatus(`The Minecraft server kicked you. Kick reason: ${kickReasonString}`, true, undefined, undefined, kickReasonFormatted)
    appStatusState.showReconnect = true
    handleSessionEnd(true)
  })

  const packetBeforePlay = (_, __, ___, fullBuffer) => {
    lastPacket = fullBuffer.toString()
  }
  bot._client.on('packet', packetBeforePlay as any)
  const playStateSwitch = (newState) => {
    if (newState === 'play') {
      bot._client.removeListener('packet', packetBeforePlay)
    }
  }
  bot._client.on('state', playStateSwitch)

  bot.on('end', (endReason) => {
    if (ended) return
    console.log('disconnected for', endReason)
    // close all modals
    for (const modal of activeModalStack) {
      hideModal(modal)
    }
    const shouldHideDisconnectScreen = miscUiState.gameLoaded || lastConnectOptions.hadWorldLoaded
    if (shouldHideDisconnectScreen) {
      handleSessionEnd()
      returnToMainMenuAfterDisconnect()
      return
    }

    setLoadingScreenStatus(`You have been disconnected from the server. End reason:\n${endReason}`, true)
    appStatusState.showReconnect = true
    onPossibleErrorDisconnect()
    handleSessionEnd()
    if (isCypress()) throw new Error(`disconnected: ${endReason}`)
  })

  onBotCreate()

  let displayWorldTriggered = false
  let displayWorldFn: (() => Promise<void>) | null = null
  let displayWorldPending = false
  const triggerDisplayWorld = () => {
    if (displayWorldTriggered) return
    if (!displayWorldFn) {
      displayWorldPending = true
      return
    }
    displayWorldTriggered = true
    void displayWorldFn()
  }

  bot.once('login', () => {
    appStatusState.waitingForPlay = false
    appStatusState.startingPlayback = false
    miscUiState.hadConnected = true
    errorAbortController.abort()
    loadingTimerState.networkOnlyStart = 0
    progress.setMessage('Loading world')

    if (connectOptions.worldStateFileContents) {
      triggerDisplayWorld()
    }
  })

  let worldWasReady = false
  const waitForChunksToLoad = async (progress?: ProgressReporter) => {
    await new Promise<void>(resolve => {
      if (worldWasReady) {
        resolve()
        return
      }
      const unsub = subscribe(appViewer.rendererState, () => {
        if (appViewer.rendererState.world.allChunksLoaded && appViewer.nonReactiveState.world.chunksTotalNumber) {
          worldWasReady = true
          resolve()
          unsub()
        } else {
          const perc = Math.round(appViewer.rendererState.world.chunksLoaded.size / appViewer.nonReactiveState.world.chunksTotalNumber * 100)
          progress?.reportProgress('chunks', perc / 100)
        }
      })
    })
  }

  const spawnEarlier = true
  const displayWorld = async () => {
    if (resourcePackState.isServerInstalling) {
      await new Promise<void>(resolve => {
        subscribe(resourcePackState, () => {
          if (!resourcePackState.isServerInstalling) {
            resolve()
          }
        })
      })
      await appViewer.resourcesManager.promiseAssetsReady
    }
    if (appStatusState.isError) return

    if (!appViewer.resourcesManager.currentResources?.itemsRenderer) {
      await appViewer.resourcesManager.updateAssetsData({})
    }

    const loadWorldStart = Date.now()
    console.log('try to focus window')
    window.focus?.()
    void waitForChunksToLoad().then(() => {
      window.worldLoadTime = (Date.now() - loadWorldStart) / 1000
      console.log('All chunks done and ready! Time from renderer connect to ready', (Date.now() - loadWorldStart) / 1000, 's')
      document.dispatchEvent(new Event('cypress-world-ready'))
    })

    try {
      playerState.reactive.onlineMode = false

      progress.setMessage('Placing blocks (starting viewer)')
      if (!connectOptions.worldStateFileContents || connectOptions.worldStateFileContents.length < 3 * 1024 * 1024) {
        localStorage.lastConnectOptions = JSON.stringify(connectOptions)
        if (process.env.NODE_ENV === 'development' && !localStorage.lockUrl && !location.search.slice(1).length) {
          lockUrl()
        }
      } else {
        localStorage.removeItem('lastConnectOptions')
      }
      connectOptions.onSuccessfulPlay?.()

      if (connectOptions.worldStateFileContents) {
        const start = Date.now()
        while (!bot.world && Date.now() - start < 15000) {
          await new Promise(resolve => setTimeout(resolve, 50))
        }
        if (!bot.world) {
          throw new Error('Bot world is not initialized')
        }
      }

      console.log('bot spawned - starting viewer')
      await appViewer.startWorld(bot.world, renderDistance)
      appViewer.worldView!.listenToBot(bot)
      if (appViewer.backend) {
        if (!bot?.entity?.position) {
          const start = Date.now()
          while (!bot?.entity?.position && Date.now() - start < 5000) {
            await new Promise(resolve => setTimeout(resolve, 50))
          }
        }
        void appViewer.worldView!.init(bot?.entity?.position ?? new Vec3(0, 64, 0))
      }

      initMotionTracking()

      // Bot position callback
      const botPosition = () => {
        if (!bot?.entity?.position) return
        appViewer.lastCamUpdate = Date.now()
        // this might cause lag, but not sure
        if (connectOptions.worldStateFileContents) {
          if (playerState.reactive.perspective === 'first_person') {
            appViewer.backend?.updateCamera(bot.entity.position, bot.entity.yaw, bot.entity.pitch)
          } else {
            const { yaw, pitch } = getReplayOrbitCameraAngles(bot.entity.yaw, bot.entity.pitch)
            appViewer.backend?.updateCamera(bot.entity.position, yaw, pitch)
          }
        } else {
          appViewer.backend?.updateCamera(bot.entity.position, bot.entity.yaw, bot.entity.pitch)
        }
        void appViewer.worldView?.updatePosition(bot.entity.position)
      }
      bot.on('move', botPosition)
      botPosition()

      progress.setMessage('Setting callbacks')

      onGameLoad()

      if (appStatusState.isError) return

      const waitForChunks = async () => {
        if (appQueryParams.sp === '1') return //todo
        if (connectOptions.worldStateFileContents) return
        const waitForChunks = options.waitForChunksRender === 'sp-only' ? false : options.waitForChunksRender
        if (!appViewer.backend || appViewer.rendererState.world.allChunksLoaded || !waitForChunks) {
          return
        }

        await progress.executeWithMessage(
          'Loading chunks',
          'chunks',
          async () => {
            await waitForChunksToLoad(progress)
          }
        )
      }

      await waitForChunks()

      miscUiState.gameLoaded = true
      miscUiState.loadedServerIndex = connectOptions.serverIndex ?? ''
      customEvents.emit('gameLoaded')

      // Test iOS Safari crash by creating memory pressure
      if (appQueryParams.testIosCrash) {
        setTimeout(() => {
          console.log('Starting iOS crash test with memory pressure...')
          // eslint-disable-next-line sonarjs/no-unused-collection
          const arrays: number[][] = []
          try {
            // Create large arrays until we run out of memory
            // eslint-disable-next-line no-constant-condition
            while (true) {
              const arr = Array.from({ length: 1024 * 1024 }).fill(0).map((_, i) => i)
              arrays.push(arr)
            }
          } catch (e) {
            console.error('Memory allocation failed:', e)
          }
        }, 1000)
      }

      progress.end()
      setLoadingScreenStatus(undefined)
    } catch (err) {
      handleError(err)
    }
    lastConnectOptions.hadWorldLoaded = true
  }

  displayWorldFn = displayWorld
  if (displayWorldPending) {
    triggerDisplayWorld()
  }

  // don't use spawn event, player can be dead
  bot.once(spawnEarlier ? 'forcedMove' : 'health', triggerDisplayWorld)

  if (!connectOptions.ignoreQs || process.env.NODE_ENV === 'development') {
    // todo cleanup
    customEvents.on('gameLoaded', () => {
      const commands = appQueryParamsArray.command ?? []
      for (let command of commands) {
        if (!command.startsWith('/')) command = `/${command}`
        const builtinHandled = tryHandleBuiltinCommand(command)
        if (!builtinHandled) {
          bot.chat(command)
        }
      }
    })
  }
}

listenGlobalEvents()

// #region fire click event on touch as we disable default behaviors
let activeTouch: { touch: Touch, elem: HTMLElement, start: number } | undefined
document.body.addEventListener('touchend', (e) => {
  if (!isGameActive(true)) return
  if (activeTouch?.touch.identifier !== e.changedTouches[0].identifier) return
  if (Date.now() - activeTouch.start > 500) {
    activeTouch.elem.dispatchEvent(new Event('longtouch', { bubbles: true }))
  } else {
    activeTouch.elem.click()
  }
  activeTouch = undefined
})
document.body.addEventListener('touchstart', (e) => {
  const targetElement = (e.target as HTMLElement).closest('#ui-root')
  if (!isGameActive(true) || !targetElement) return
  // we always prevent default behavior to disable magnifier on ios, but by doing so we also disable click events
  e.preventDefault()
  let firstClickable // todo remove composedPath and this workaround when lit-element is fully dropped
  const path = e.composedPath() as Array<{ click?: () => void }>
  for (const elem of path) {
    if (elem.click) {
      firstClickable = elem
      break
    }
  }
  if (!firstClickable) return
  activeTouch = {
    touch: e.touches[0],
    elem: firstClickable,
    start: Date.now(),
  }
}, { passive: false })
// #endregion

// replay-first startup actions
const maybeEnterGame = () => {
  if (isInterestedInDownload()) {
    void downloadAndOpenFile()
  }
}

// Skip game connection logic in playground mode
if (!isPlayground) {
  try {
    maybeEnterGame()
  } catch (err) {
    console.error(err)
    alert(`Something went wrong: ${err}`)
  }
}

// eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion
const initialLoader = document.querySelector('.initial-loader') as HTMLElement | null
if (initialLoader) {
  const hideInitialLoader = () => {
    initialLoader.style.opacity = '0'
    initialLoader.style.pointerEvents = 'none'
  }
  const hideInitialLoaderOnNextPaint = () => {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        hideInitialLoader()
      })
    })
  }

  const startupUiReadySelector = "[data-replay-main-menu='true'], .app-status-screen"
  if (document.querySelector(startupUiReadySelector)) {
    hideInitialLoaderOnNextPaint()
  } else {
    const observer = new MutationObserver(() => {
      if (document.querySelector(startupUiReadySelector)) {
        hideInitialLoaderOnNextPaint()
        observer.disconnect()
      }
    })
    observer.observe(document.body, {
      childList: true,
      subtree: true,
    })

    setTimeout(() => {
      hideInitialLoader()
      observer.disconnect()
    }, 5000)
  }
}
window.pageLoaded = true

if (!isPlayground) {
  appViewer.waitBackendLoadPromises.push(appStartup())
}
